import { createAsyncThunk } from '@reduxjs/toolkit';
import { getAccounts, getUser, patchUser, switchAccount } from './api';
import { AppDispatch, RootState } from '../../configuration/setup/store';
import { getAccessToken, getIdToken } from '../../configuration/tokenHandling/tokenHandlingSlice';
import { Account, APIUser, PatchedUser, User } from './schemas';
import { isLeft } from 'fp-ts/Either';
import mapKeys from 'lodash/fp/mapKeys';
import camelCase from 'lodash/fp/camelCase';
import { selectUser } from './appSlice';
import { UserInfo } from '../../configuration/types';
import { languageChanged, profileChanged, sendMessageToIFrameWrapper } from '../common/iFrameHelper';
import { getLanguageData } from '../../configuration/lang/services';
import { displayMessagesFetched, localeChanged } from '../../configuration/lang/langSlice';

const sendUserInfoToEmbeddingComponent = (user: User, userName: string) => {
    if ('parentIFrame' in window) {
        const userInfo: UserInfo = {
            firstName: user.firstName,
            lastName: user.lastName,
            username: userName,
        };
        sendMessageToIFrameWrapper(profileChanged(userInfo));
    }
};

const sendLocaleChangeToEmbeddingComponent = (locale: string) => {
    if ('parentIFrame' in window) {
        sendMessageToIFrameWrapper(languageChanged(locale));
    }
};

// --------------------------------------------------
// Converters
const convertAPIUserToUser = (apiUser: APIUser): User => {
    const convertedUser = mapKeys(camelCase, apiUser);
    delete convertedUser['metadata'];
    delete convertedUser['links'];

    if (!isUser(convertedUser)) {
        throw new Error('invalid user');
    }

    return convertedUser;
};

const userToPatchedUser = (user: User): PatchedUser => {
    const patchedUser = {
        id: user.id,
        account_id: user.accountId,
        first_name: user.firstName,
        last_name: user.lastName,
        preferred_language: user.preferredLanguage,
    };

    if (!isPatchedUser(patchedUser)) {
        throw new Error('invalid user');
    }

    return patchedUser;
};

// --------------------------------------------------
// type guards
const isAPIUser = (obj: any): obj is APIUser =>
    typeof obj.id === 'string' &&
    typeof obj.first_name === 'string' &&
    typeof obj.last_name === 'string' &&
    typeof obj.group_ids[0] === 'string';

const isPatchedUser = (obj: any): obj is PatchedUser =>
    typeof obj.id === 'string' &&
    typeof obj.first_name === 'string' &&
    typeof obj.last_name === 'string' &&
    typeof obj.account_id === 'string' &&
    typeof obj.preferred_language === 'string';

const isUser = (obj: any): obj is User =>
    typeof obj.id === 'string' && typeof obj.firstName === 'string' && typeof obj.lastName === 'string';

// --------------------------------------------------
// Util

const delay = (delayInMs: number) => new Promise((resolve) => setTimeout(resolve, delayInMs));

const mergePatchedUserWithUser = (existingUser: User, patchedUser: PatchedUser): User => ({
    ...existingUser,
    firstName: patchedUser.first_name,
    lastName: patchedUser.last_name,
    preferredLanguage: patchedUser.preferred_language,
});

// --------------------------------------------------
// Thunks

export const fetchUserThunk = createAsyncThunk<User, string, { state: RootState }>(
    'app/fetchUser',
    async (userId: string, thunkAPI) => {
        const state = thunkAPI.getState();
        const token = getAccessToken(state);

        if (token == null) {
            return Promise.reject(new Error('userError: no access token available'));
        }

        try {
            const res = await getUser(userId, token);
            if (isLeft(APIUser.decode(res)) || !isAPIUser(res)) {
                return Promise.reject(new Error('invalid API response'));
            }
            return convertAPIUserToUser(res);
        } catch (e) {
            console.warn(`getUser failed with:`, e);
            return Promise.reject(new Error(`userError: ${JSON.stringify(e)}`));
        }
    }
);

export const patchUserThunk = createAsyncThunk<User, { firstName: string; lastName: string }, { state: RootState }>(
    'app/patchUser',
    async (arg: { firstName: string; lastName: string }, thunkAPI) => {
        const state = thunkAPI.getState();
        const token = getAccessToken(state);
        const idToken = getIdToken(state);

        if (token == null) {
            return Promise.reject(new Error('userError: no access token'));
        }

        const user = { ...selectUser(state) }; // clone object, because state object is immutable
        const userName = idToken?.username ? idToken.username : ''; // only used for communication with embedding page

        user['firstName'] = arg.firstName;
        user['lastName'] = arg.lastName;

        const patchedUser = userToPatchedUser(user);

        try {
            const res = await patchUser(user.id, patchedUser, token);

            // delay to ensure that change has propagated to users table in auth server
            // it would be cleaner, to poll the /userinfo endpoint, but currently the data there is not
            // updated (since the locale in that endpoint is tied to the authorization)
            await delay(500);

            if (!isPatchedUser(res)) {
                return Promise.reject(new Error('invalid API response'));
            }

            const updatedUser = mergePatchedUserWithUser(user, patchedUser);
            sendUserInfoToEmbeddingComponent(updatedUser, userName);
            return updatedUser;
        } catch (e) {
            console.warn(`patchUser failed with: ${e}`);
            return Promise.reject(`userError: ${JSON.stringify(e)}`);
        }
    }
);

export const patchLocaleThunk = createAsyncThunk<string, string, { state: RootState }>(
    'app/patchLocale',
    async (updatedLocale: string, thunkAPI) => {
        const state = thunkAPI.getState();
        const token = getAccessToken(state);

        if (token == null) {
            return Promise.reject(new Error('userError: no access token'));
        }

        const user = { ...selectUser(state) }; // clone object, because state object is immutable
        user['preferredLanguage'] = updatedLocale;

        const patchedUser = userToPatchedUser(user);

        try {
            const res = await patchUser(user.id, patchedUser, token);

            // delay to ensure that change has propagated to users table in auth server
            // it would be cleaner, to poll the /userinfo endpoint, but currently the data there is not
            // updated (since the locale in that endpoint is tied to the authorization)
            await delay(500);

            if (!isPatchedUser(res)) {
                return Promise.reject(new Error('invalid API response'));
            }

            thunkAPI.dispatch(localeChanged(patchedUser.preferred_language));
            const languageData = await getLanguageData(patchedUser.preferred_language);
            thunkAPI.dispatch(
                displayMessagesFetched({ locale: patchedUser.preferred_language, displayMessages: languageData })
            );
            sendLocaleChangeToEmbeddingComponent(patchedUser.preferred_language);
            return patchedUser.preferred_language;
        } catch (e) {
            console.warn('patchLocale failed with:', e);
            return Promise.reject(`localeError: ${JSON.stringify(e)}`);
        }
    }
);

export const fetchAccountsThunk = createAsyncThunk<Account[], void, { dispatch: AppDispatch; state: RootState }>(
    'app/fetchAccounts',
    async (_, thunkAPI) => {
        const state = thunkAPI.getState();
        const token = getAccessToken(state);
        const userProfileId = getIdToken(state)?.sub?.split(':')[1];

        if (token == null) {
            return Promise.reject(new Error('accountsError: no access token available'));
        }

        if (!userProfileId) {
            return Promise.reject(new Error('accountsError: could not read profileId from token'));
        }

        try {
            const res = await getAccounts(token, userProfileId);
            const json = await res.json();

            // TODO: fix types
            return json.accounts;
        } catch (e) {
            return Promise.reject(`accountsError: ${JSON.stringify(e)}`);
        }
    }
);

export const switchAccountThunk = createAsyncThunk('app/switchAccount', async (accountId: string) => {
    try {
        const res = await switchAccount(accountId);
        if (res.status < 400) {
            // TODO: RIOCC-1488 Is there a better way than hard page reload?
            window.parent.location.href = document.referrer ? document.referrer : 'https://home.rio.cloud';
            return;
        } else {
            return Promise.reject(`accountError: received status code ${res.status}`);
        }
    } catch (e) {
        return Promise.reject(`accountError: ${JSON.stringify(e)}`);
    }
});
